/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import { CentralIaFile, CentralIaState } from 'store/modules/central_ia/types';
import {
  addUserMessage,
  deleteChat,
  generateImage,
  moveChat,
  newChat,
  removeChatFile,
  removePendingFile,
  sendChatMessageRequest,
  chatStreamStart,
  chatStreamChunk,
  chatStreamDone,
  chatStreamTitle,
  chatStreamError,
  chatStreamStopped,
  chatFilesMissing,
  setProvider,
  setModel,
  startChat,
  updateChat,
  uploadFile,
} from 'store/modules/central_ia/actions';

import CentralIaProviderSelector, {
  providers,
  getDefaultVersion,
  getVersionLabel,
} from 'components/molecules/central_ia_provider_selector';
import CentralIaComposerMenu, {
  ImageProviderId,
} from 'components/molecules/central_ia_composer_menu';
import StarIcon from 'assets/images/central_ia/star.svg';
import { streamCentralIaMessage } from 'services/central_ia_stream';
import { prepareAssistantMarkdown } from 'utils/markdown';
import MarkdownCode from './markdown_code';
import {
  Container,
  ChatView,
  ProviderDropdown,
  ProviderHeader,
  ChatInputBar,
  MessageRow,
  Avatar,
  AssistantIcon,
  TypingDots,
  ChatTopBar,
  MarkdownContent,
  GeneratedImage,
  FileDropOverlay,
} from './styles';

const TYPING_STATUS_MESSAGES = [
  'Pensando...',
  'Analisando sua pergunta...',
  'Consultando informações...',
  'Gerando resposta...',
  'Quase lá...',
];

const WEB_SEARCH_STATUS_MESSAGES = [
  'Pesquisando na web...',
  'Lendo as fontes encontradas...',
  'Organizando a resposta...',
];

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

interface CentralIaChatAreaProps {
  prefillPrompt?: string | null;
  onPrefillConsumed?: () => void;
}

const CentralIaChatArea: React.FC<CentralIaChatAreaProps> = ({
  prefillPrompt = null,
  onPrefillConsumed,
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [providerMenuOpen, setProviderMenuOpen] = useState(false);
  const [typingStatusIdx, setTypingStatusIdx] = useState(0);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [chatMenuView, setChatMenuView] = useState<'main' | 'move' | 'rename'>('main');
  const [renameChatValue, setRenameChatValue] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [pdfMessageId, setPdfMessageId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const providerDropdownRef = useRef<HTMLDivElement>(null);
  const chatMenuRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [webSearchActive, setWebSearchActive] = useState(false);
  const [imageMode, setImageMode] = useState<ImageProviderId | null>(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const streamControllerRef = useRef<AbortController | null>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const [respondingWithWebSearch, setRespondingWithWebSearch] = useState(false);
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const {
    messages,
    request,
    selectedProvider,
    selectedModel,
    chatStarted,
    currentChatId,
    chats,
    folders,
    pendingFiles,
    chatFiles,
    lastError,
  } = useSelector<ApplicationState, CentralIaState>((state) => state.session.central_ia);
  const currentChat = (chats || []).find((c) => c.id === currentChatId);
  const folderList = folders || [];
  const pendingFileList = pendingFiles || [];
  const chatFileList = chatFiles || [];
  const isUploading = request?.upload_file?.loading;

  const firstName = profile?.name?.split(' ')[0] || 'Usuário';
  const userInitial = profile?.name?.charAt(0).toUpperCase() || 'U';
  const currentProvider = providers.find(
    (p) => p.id === selectedProvider || p.model === selectedProvider,
  );
  const currentVersionLabel = getVersionLabel(selectedModel) || currentProvider?.name || '';
  const isResponding = request.send_chat_message.loading;
  const isGeneratingImage = request?.generate_image?.loading;
  const hasError = request.send_chat_message?.error;
  const imageError = request?.generate_image?.error;
  const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
  const lastMessage = messages[messages.length - 1];
  const isStreamingText = !!lastMessage?.streaming;
  const statusMessages = respondingWithWebSearch
    ? WEB_SEARCH_STATUS_MESSAGES
    : TYPING_STATUS_MESSAGES;

  useEffect(() => {
    const list = messagesListRef.current;
    if (list && stickToBottomRef.current) list.scrollTop = list.scrollHeight;
  }, [messages, isResponding, hasError, imageError, isGeneratingImage]);

  const handleMessagesScroll = () => {
    const list = messagesListRef.current;
    if (!list) return;
    stickToBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 120;
  };

  useEffect(() => {
    if (isResponding && !streamControllerRef.current) dispatch(chatStreamStopped());
    return () => streamControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!isResponding) {
      setTypingStatusIdx(0);
      return undefined;
    }
    const id = window.setInterval(() => {
      setTypingStatusIdx((idx) => (idx + 1) % statusMessages.length);
    }, 2000);
    return () => window.clearInterval(id);
  }, [isResponding, statusMessages.length]);

  useEffect(() => {
    if (!providerMenuOpen && !chatMenuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        providerMenuOpen &&
        providerDropdownRef.current &&
        !providerDropdownRef.current.contains(target)
      ) {
        setProviderMenuOpen(false);
      }
      if (chatMenuOpen && chatMenuRef.current && !chatMenuRef.current.contains(target)) {
        setChatMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [providerMenuOpen, chatMenuOpen]);

  useEffect(() => {
    if (copiedMessageId === null) return undefined;
    const id = window.setTimeout(() => setCopiedMessageId(null), 2000);
    return () => window.clearTimeout(id);
  }, [copiedMessageId]);

  useEffect(() => {
    if (!prefillPrompt) return;
    setInputValue(prefillPrompt);
    onPrefillConsumed?.();
  }, [prefillPrompt]);

  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [inputValue]);

  const handleSelectVersion = (providerId: string, model: string) => {
    dispatch(setProvider(providerId));
    dispatch(setModel(model));
    setProviderMenuOpen(false);
  };

  const handleToggleProviderMenu = () => {
    setProviderMenuOpen((open) => !open);
  };

  const handleSelectProvider = (providerId: string) => {
    dispatch(setProvider(providerId));
    dispatch(setModel(getDefaultVersion(providerId)));
  };

  const handleStartChat = () => {
    if (!selectedProvider) return;
    dispatch(startChat());
  };

  const handleToggleChatMenu = () => {
    setChatMenuView('main');
    setChatMenuOpen((open) => !open);
  };

  const handleStartChatRename = () => {
    if (!currentChat) return;
    setRenameChatValue(currentChat.title);
    setChatMenuView('rename');
  };

  const handleSubmitChatRename = () => {
    if (!currentChatId) return;
    const trimmed = renameChatValue.trim();
    if (!trimmed) return;
    dispatch(updateChat(currentChatId, trimmed));
    setChatMenuOpen(false);
    setChatMenuView('main');
  };

  const handleChatMove = (folderId: number | null) => {
    if (!currentChatId) return;
    dispatch(moveChat(currentChatId, folderId));
    setChatMenuOpen(false);
    setChatMenuView('main');
  };

  const handleChatDelete = () => {
    if (!currentChatId) return;
    dispatch(deleteChat(currentChatId));
    dispatch(newChat());
    setChatMenuOpen(false);
  };

  const sendPrompt = async (prompt: string) => {
    if (!currentProvider) return;
    const controller = new AbortController();
    streamControllerRef.current = controller;
    const filesById = new Map<string, CentralIaFile>();
    [...chatFileList, ...pendingFileList].forEach((file) =>
      filesById.set(String(file.file_id), file),
    );
    const model = getVersionLabel(selectedModel)
      ? selectedModel
      : getDefaultVersion(currentProvider.id);

    setRespondingWithWebSearch(webSearchActive);
    stickToBottomRef.current = true;
    dispatch(sendChatMessageRequest());
    const outcome = await streamCentralIaMessage(
      {
        provider: currentProvider.model,
        model: model || undefined,
        prompt,
        chat_id: currentChatId,
        web_search: webSearchActive,
        files: Array.from(filesById.values()),
      },
      (event) => {
        if (event.type === 'start') dispatch(chatStreamStart(event.chat, event.user_message));
        if (event.type === 'chunk' && event.content) dispatch(chatStreamChunk(event.content));
        if (event.type === 'files_missing') dispatch(chatFilesMissing(event.file_ids || []));
        if (event.type === 'done') dispatch(chatStreamDone(event.chat, event.assistant_message));
        if (event.type === 'title' && event.chat_id && event.title) {
          dispatch(chatStreamTitle(event.chat_id, event.title));
        }
        if (event.type === 'error') dispatch(chatStreamError(event.message, event.chat));
      },
      controller.signal,
    );
    if (outcome === 'aborted') dispatch(chatStreamStopped());
    if (streamControllerRef.current === controller) streamControllerRef.current = null;
  };

  const handleStopResponse = () => {
    streamControllerRef.current?.abort();
  };

  const handleRetry = () => {
    if (!currentProvider || !lastUserMessage || isResponding) return;
    sendPrompt(lastUserMessage.content);
  };

  const handleCopyMessage = async (messageId: number, content: string) => {
    if (!content) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = content;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setCopiedMessageId(messageId);
    } catch {
      setCopiedMessageId(null);
    }
  };

  const handleDownloadPdf = async (messageId: number, content: string) => {
    if (!content || pdfMessageId !== null) return;
    setPdfMessageId(messageId);
    try {
      const { downloadMessagePdf } = await import('./message_pdf');
      await downloadMessagePdf(content, currentChat?.title);
    } finally {
      setPdfMessageId(null);
    }
  };

  const handleDownloadImage = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `central-ia-imagem-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSendMessage = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || !currentProvider || isResponding || isGeneratingImage) return;
    if (imageMode) {
      dispatch(addUserMessage(trimmed));
      dispatch(generateImage({ prompt: trimmed }));
      setInputValue('');
      return;
    }
    dispatch(addUserMessage(trimmed));
    sendPrompt(trimmed);
    setInputValue('');
  };

  const handleAttachClick = () => {
    if (isResponding || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleToggleMic = () => {
    const SpeechRecognitionCtor =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join(' ')
        .trim();
      if (transcript) {
        setInputValue((current) =>
          current.trim() ? `${current.trim()} ${transcript}` : transcript,
        );
      }
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    files.forEach((file) => dispatch(uploadFile(file)));
    if (event.target) event.target.value = '';
  };

  const handleRemovePendingFile = (fileId: string | number) => {
    dispatch(removePendingFile(fileId));
  };

  const handleRemoveChatFile = (fileId: string | number) => {
    dispatch(removeChatFile(fileId));
  };

  const getFileTypeInfo = (fileName: string, contentType?: string) => {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    if (ext === 'pdf' || contentType?.includes('pdf')) {
      return { label: 'PDF', color: '#e23b3b', icon: 'description' };
    }
    if (['doc', 'docx'].includes(ext) || contentType?.includes('word')) {
      return { label: 'Word', color: '#2b6cb0', icon: 'description' };
    }
    if (['xls', 'xlsx', 'csv'].includes(ext) || contentType?.includes('sheet')) {
      return { label: 'Planilha', color: '#2f855a', icon: 'description' };
    }
    if (
      ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) ||
      contentType?.startsWith('image/')
    ) {
      return { label: 'Imagem', color: '#805ad5', icon: 'image' };
    }
    if (ext === 'txt' || contentType === 'text/plain') {
      return { label: 'Texto', color: '#718096', icon: 'description' };
    }
    return { label: ext.toUpperCase() || 'Arquivo', color: '#4a5568', icon: 'description' };
  };

  const hasFileType = (event: React.DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types || []).includes('Files');

  const handleContainerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileType(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsFileDragOver(true);
  };

  const handleContainerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileType(event)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsFileDragOver(false);
  };

  const handleContainerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileType(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleContainerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileType(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsFileDragOver(false);
    if (isResponding || isUploading) return;
    const files = Array.from(event.dataTransfer.files || []);
    files.forEach((file) => dispatch(uploadFile(file)));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const renderProviderDropdown = (extraClass = '') =>
    currentProvider && (
      <ProviderDropdown ref={providerDropdownRef} className={`dropdown ${extraClass}`}>
        <ProviderHeader
          type="button"
          className="btn d-flex align-items-center px-3 py-1"
          onClick={handleToggleProviderMenu}
          aria-haspopup="true"
          aria-expanded={providerMenuOpen}
        >
          <img src={currentProvider.icon} alt={currentProvider.name} className="mr-2" />
          <span className="font-weight-light mr-1">{currentVersionLabel}</span>
          <i className="material-icons">expand_more</i>
        </ProviderHeader>
        <div className={`dropdown-menu mt-1 ${providerMenuOpen ? 'show' : ''}`}>
          {currentProvider.versions.map((version) => (
            <button
              key={version.id}
              type="button"
              className={`dropdown-item ${version.id === selectedModel ? 'active' : ''}`}
              onClick={() => handleSelectVersion(currentProvider.id, version.id)}
            >
              <span className="font-weight-light">{version.label}</span>
            </button>
          ))}
        </div>
      </ProviderDropdown>
    );

  if (chatStarted && currentProvider) {
    const hasMessages = messages.length > 0;
    return (
      <Container
        className="d-flex flex-column align-items-center p-4 bg-white"
        onDragEnter={handleContainerDragEnter}
        onDragLeave={handleContainerDragLeave}
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
      >
        {isFileDragOver && (
          <FileDropOverlay>
            <div className="overlay-content text-center">
              <i className="material-icons mb-2">cloud_upload</i>
              <div className="font-weight-light">Solte o arquivo aqui</div>
            </div>
          </FileDropOverlay>
        )}
        <ChatView
          className={`d-flex flex-column align-items-center w-100 ${
            !hasMessages ? 'justify-content-center' : 'justify-content-between'
          }`}
        >
          {!hasMessages ? (
            <>
              {renderProviderDropdown('start-corner')}
              <h2 className="greeting-title m-0 mb-4">Como posso ajudar, {firstName}?</h2>
            </>
          ) : (
            <>
              <ChatTopBar className="d-flex align-items-center justify-content-between w-100">
                <div className="topbar-inner d-flex align-items-center justify-content-between mx-auto">
                  {renderProviderDropdown()}
                  <div className="dropdown" ref={chatMenuRef}>
                    <button
                      type="button"
                      className="btn p-1 menu-button"
                      title="Opções"
                      onClick={handleToggleChatMenu}
                    >
                      <i className="material-icons">more_vert</i>
                    </button>
                    <div
                      className={`dropdown-menu dropdown-menu-right mt-1 ${
                        chatMenuOpen ? 'show' : ''
                      }`}
                    >
                      {chatMenuView === 'main' && (
                        <>
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center"
                            onClick={handleStartChatRename}
                          >
                            <i className="material-icons mr-2">edit</i>
                            Renomear chat
                          </button>
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setChatMenuView('move')}
                          >
                            <i className="material-icons mr-2">create_new_folder</i>
                            Mover para o projeto
                          </button>
                          <div className="dropdown-divider" />
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center text-danger"
                            onClick={handleChatDelete}
                          >
                            <i className="material-icons mr-2">close</i>
                            Excluir
                          </button>
                        </>
                      )}
                      {chatMenuView === 'move' && (
                        <>
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setChatMenuView('main')}
                          >
                            <i className="material-icons mr-2">arrow_back</i>
                            Mover para
                          </button>
                          <div className="dropdown-divider" />
                          {currentChat?.central_ia_folder_id !== null && (
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => handleChatMove(null)}
                            >
                              <i className="material-icons mr-2">remove_circle_outline</i>
                              Remover do projeto
                            </button>
                          )}
                          {folderList.length === 0 ? (
                            <div className="dropdown-item-text text-muted font-italic small">
                              Nenhum projeto criado
                            </div>
                          ) : (
                            folderList
                              .filter(
                                (f) =>
                                  String(f.id) !== String(currentChat?.central_ia_folder_id ?? ''),
                              )
                              .map((f) => (
                                <button
                                  key={f.id}
                                  type="button"
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => handleChatMove(Number(f.id))}
                                >
                                  <i className="material-icons mr-2">folder</i>
                                  {f.name}
                                </button>
                              ))
                          )}
                        </>
                      )}
                      {chatMenuView === 'rename' && (
                        <div className="px-2 py-2" style={{ minWidth: 220 }}>
                          <input
                            type="text"
                            className="form-control form-control-sm font-weight-light mb-2"
                            value={renameChatValue}
                            onChange={(event) => setRenameChatValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSubmitChatRename();
                              } else if (event.key === 'Escape') {
                                setChatMenuView('main');
                              }
                            }}
                            autoFocus
                            maxLength={120}
                          />
                          <div className="d-flex justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-muted mr-2"
                              onClick={() => setChatMenuView('main')}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleSubmitChatRename}
                              disabled={!renameChatValue.trim()}
                            >
                              Salvar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </ChatTopBar>

              <div
                ref={messagesListRef}
                onScroll={handleMessagesScroll}
                className="messages-list flex-grow-1 w-100 d-flex flex-column"
              >
                <div className="messages-inner mx-auto w-100 py-4 d-flex flex-column">
                  {messages.map((message) =>
                    message.role === 'user' ? (
                      <MessageRow
                        key={message.id}
                        className="d-flex align-items-end justify-content-end mb-3"
                      >
                        <div className="message message-user px-3 py-2 rounded mr-2">
                          {message.content}
                        </div>
                        {profile?.avatar_url ? (
                          <Avatar as="img" src={profile.avatar_url} alt={firstName} />
                        ) : (
                          <Avatar className="d-flex align-items-center justify-content-center">
                            {userInitial}
                          </Avatar>
                        )}
                      </MessageRow>
                    ) : (
                      <MessageRow
                        key={message.id}
                        className="d-flex align-items-start justify-content-start mb-3"
                      >
                        <AssistantIcon className="d-flex align-items-center justify-content-center mr-2">
                          <img src={StarIcon} alt="Central IA" />
                        </AssistantIcon>
                        {message.imageUrl ? (
                          <GeneratedImage className="message message-assistant p-2 rounded">
                            <img
                              src={message.imageUrl}
                              alt="Imagem gerada"
                              className="generated-image"
                            />
                            <button
                              type="button"
                              className="download-image btn"
                              onClick={() => handleDownloadImage(message.imageUrl as string)}
                              title="Baixar imagem"
                              aria-label="Baixar imagem"
                            >
                              <i className="material-icons">download</i>
                            </button>
                          </GeneratedImage>
                        ) : (
                          <div className="assistant-block d-flex flex-column">
                            <MarkdownContent className="message message-assistant px-3 py-2 rounded">
                              <ReactMarkdown
                                remarkPlugins={REMARK_PLUGINS}
                                rehypePlugins={REHYPE_PLUGINS}
                                components={{
                                  a: ({ children, ...anchorProps }) => (
                                    <a {...anchorProps} target="_blank" rel="noopener noreferrer">
                                      {children}
                                    </a>
                                  ),
                                  code: MarkdownCode,
                                  pre: ({ children }) => (
                                    <div className="code-block">{children}</div>
                                  ),
                                  table: ({ children }) => (
                                    <div className="table-scroll">
                                      <table>{children}</table>
                                    </div>
                                  ),
                                }}
                              >
                                {prepareAssistantMarkdown(message.content)}
                              </ReactMarkdown>
                              {message.interrupted && (
                                <small className="interrupted-note d-block mt-2">
                                  Resposta interrompida.
                                </small>
                              )}
                              {!!message.citations?.length && (
                                <div className="citations mt-2 pt-2">
                                  <span className="citations-title d-block mb-1">Fontes</span>
                                  <ol className="mb-0 pl-3">
                                    {message.citations.map((citation) => (
                                      <li key={citation.url}>
                                        <a
                                          href={citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {citation.title || citation.url}
                                        </a>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </MarkdownContent>
                            {!!message.content && !message.streaming && (
                              <div className="message-actions d-flex align-items-center mt-1">
                                <button
                                  type="button"
                                  className={`copy-button d-flex align-items-center ${
                                    copiedMessageId === message.id ? 'copied' : ''
                                  }`}
                                  onClick={() => handleCopyMessage(message.id, message.content)}
                                  title={
                                    copiedMessageId === message.id ? 'Copiado' : 'Copiar resposta'
                                  }
                                  aria-label="Copiar resposta"
                                >
                                  <i className="material-icons">
                                    {copiedMessageId === message.id ? 'check' : 'content_copy'}
                                  </i>
                                </button>
                                <button
                                  type="button"
                                  className="copy-button d-flex align-items-center"
                                  onClick={() => handleDownloadPdf(message.id, message.content)}
                                  disabled={pdfMessageId !== null}
                                  title="Baixar resposta em PDF"
                                  aria-label="Baixar resposta em PDF"
                                >
                                  {pdfMessageId === message.id ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                    >
                                      <span className="sr-only">Gerando PDF...</span>
                                    </span>
                                  ) : (
                                    <i className="material-icons">picture_as_pdf</i>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </MessageRow>
                    ),
                  )}
                  {isResponding && !isStreamingText && (
                    <MessageRow className="d-flex align-items-center justify-content-start mb-3">
                      <AssistantIcon className="d-flex align-items-center justify-content-center mr-2 pulsing">
                        <img src={StarIcon} alt="Central IA" />
                      </AssistantIcon>
                      <TypingDots className="d-flex align-items-center px-3 py-2 rounded">
                        <span className="dots">
                          <span />
                          <span />
                          <span />
                        </span>
                        <span className="status-text">
                          {statusMessages[typingStatusIdx % statusMessages.length]}
                        </span>
                      </TypingDots>
                    </MessageRow>
                  )}
                  {isGeneratingImage && (
                    <MessageRow className="d-flex align-items-center justify-content-start mb-3">
                      <AssistantIcon className="d-flex align-items-center justify-content-center mr-2 pulsing">
                        <img src={StarIcon} alt="Central IA" />
                      </AssistantIcon>
                      <TypingDots className="d-flex align-items-center px-3 py-2 rounded">
                        <span className="dots">
                          <span />
                          <span />
                          <span />
                        </span>
                        <span className="status-text">Gerando imagem...</span>
                      </TypingDots>
                    </MessageRow>
                  )}
                  {imageError && !isGeneratingImage && (
                    <MessageRow className="d-flex align-items-start justify-content-start mb-3">
                      <AssistantIcon className="d-flex align-items-center justify-content-center mr-2">
                        <img src={StarIcon} alt="Central IA" />
                      </AssistantIcon>
                      <div className="message message-error px-3 py-2 rounded d-flex align-items-center">
                        <i className="material-icons mr-2">error_outline</i>
                        <span>Não foi possível gerar a imagem.</span>
                      </div>
                    </MessageRow>
                  )}
                  {hasError && !isResponding && (
                    <MessageRow className="d-flex align-items-start justify-content-start mb-3">
                      <AssistantIcon className="d-flex align-items-center justify-content-center mr-2">
                        <img src={StarIcon} alt="Central IA" />
                      </AssistantIcon>
                      <div className="message message-error px-3 py-2 rounded d-flex align-items-center">
                        <i className="material-icons mr-2">error_outline</i>
                        <span className="mr-2">
                          {lastError || 'Não foi possível obter resposta.'}
                        </span>
                        {lastUserMessage && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-danger font-weight-bold"
                            onClick={handleRetry}
                          >
                            Tentar novamente
                          </button>
                        )}
                      </div>
                    </MessageRow>
                  )}
                </div>
              </div>
            </>
          )}

          {(chatFileList.length > 0 || pendingFileList.length > 0 || isUploading) && (
            <div className="pending-files d-flex flex-wrap w-100 mb-2">
              {[
                ...chatFileList.map((file) => ({ file, inContext: true })),
                ...pendingFileList.map((file) => ({ file, inContext: false })),
              ].map(({ file, inContext }) => {
                const info = getFileTypeInfo(file.file_name, file.content_type);
                return (
                  <div
                    key={file.file_id}
                    className={`pending-file d-flex align-items-center ${
                      inContext ? 'in-context' : ''
                    }`}
                    title={
                      inContext ? `${file.file_name} - no contexto da conversa` : file.file_name
                    }
                  >
                    <div
                      className="file-icon d-flex align-items-center justify-content-center"
                      style={{ background: info.color }}
                    >
                      <i className="material-icons text-white">{info.icon}</i>
                    </div>
                    <div className="file-meta d-flex flex-column">
                      <span className="file-name font-weight-bold text-truncate">
                        {file.file_name}
                      </span>
                      <span className="file-type text-muted small">
                        {inContext ? `${info.label} - no contexto` : info.label}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() =>
                        inContext
                          ? handleRemoveChatFile(file.file_id)
                          : handleRemovePendingFile(file.file_id)
                      }
                      title={inContext ? 'Remover do contexto da conversa' : 'Remover anexo'}
                    >
                      <i className="material-icons">close</i>
                    </button>
                  </div>
                );
              })}
              {isUploading && (
                <div className="pending-file d-flex align-items-center">
                  <div
                    className="file-icon d-flex align-items-center justify-content-center"
                    style={{ background: '#cbd5e0' }}
                  >
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="sr-only">Enviando...</span>
                    </div>
                  </div>
                  <div className="file-meta d-flex flex-column">
                    <span className="file-name font-weight-bold text-truncate">
                      Enviando arquivo...
                    </span>
                    <span className="file-type text-muted small">Aguarde</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {(webSearchActive || imageMode) && (
            <div className="composer-chips d-flex flex-wrap w-100 mb-2">
              {webSearchActive && (
                <span className="composer-chip">
                  <i className="material-icons">public</i>
                  Busca na web
                  <button
                    type="button"
                    onClick={() => setWebSearchActive(false)}
                    aria-label="Desativar busca na web"
                  >
                    <i className="material-icons">close</i>
                  </button>
                </span>
              )}
              {imageMode && (
                <span className="composer-chip">
                  <i className="material-icons">image</i>
                  Criar imagem · {imageMode === 'gemini' ? 'Nano Banana' : 'GPT'}
                  <button
                    type="button"
                    onClick={() => setImageMode(null)}
                    aria-label="Sair do modo imagem"
                  >
                    <i className="material-icons">close</i>
                  </button>
                </span>
              )}
            </div>
          )}
          <ChatInputBar className="d-flex align-items-center w-100 rounded-pill bg-white shadow-sm pl-3 pr-2 py-2">
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              multiple
              onChange={handleFilesSelected}
            />
            <CentralIaComposerMenu
              disabled={isResponding || isUploading || isGeneratingImage}
              webSearchActive={webSearchActive}
              imageMode={imageMode}
              onAttachFile={handleAttachClick}
              onToggleWebSearch={() => setWebSearchActive((active) => !active)}
              onSelectImageProvider={setImageMode}
            />
            <textarea
              ref={composerRef}
              rows={1}
              className="form-control border-0 bg-transparent shadow-none composer-textarea"
              placeholder={
                imageMode ? 'Descreva a imagem que deseja criar' : 'Pergunte alguma coisa'
              }
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isResponding || isGeneratingImage}
            />
            <button
              type="button"
              className={`btn rounded-circle d-flex align-items-center justify-content-center mic-button ${
                isListening ? 'listening' : ''
              }`}
              onClick={handleToggleMic}
              disabled={isResponding || isGeneratingImage}
              title={isListening ? 'Parar gravação' : 'Falar'}
              aria-label={isListening ? 'Parar gravação' : 'Falar'}
            >
              <i className="material-icons">mic</i>
            </button>
            {isResponding || isGeneratingImage ? (
              <button
                type="button"
                className="btn rounded-circle d-flex align-items-center justify-content-center send-button stop-button"
                onClick={handleStopResponse}
                disabled={!isResponding}
                title={isResponding ? 'Parar resposta' : 'Aguardando resposta'}
                aria-label={isResponding ? 'Parar resposta' : 'Aguardando resposta'}
              >
                <i className="material-icons">stop</i>
              </button>
            ) : (
              <button
                type="button"
                className="btn rounded-circle d-flex align-items-center justify-content-center send-button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                title="Enviar"
              >
                <i className="material-icons">arrow_forward</i>
              </button>
            )}
          </ChatInputBar>
        </ChatView>
      </Container>
    );
  }

  return (
    <Container className="d-flex flex-column align-items-center justify-content-center p-4 bg-white">
      <div className="nucleo-badge d-flex align-items-center font-weight-light mb-2">
        <img src={StarIcon} alt="Central IA" className="mr-1" />
        Central IA
      </div>
      <h1 className="greeting-title mb-4">Olá, {firstName}!</h1>
      <p className="subtitle font-weight-light mb-4">Escolha a IA que deseja utilizar:</p>

      <CentralIaProviderSelector
        selectedProvider={selectedProvider}
        onSelectProvider={handleSelectProvider}
      />

      <button
        type="button"
        className="btn btn-ia btn-primary rounded-pill mt-4"
        onClick={handleStartChat}
        disabled={!selectedProvider}
      >
        Iniciar chat
      </button>
      <small className="ia-disclaimer d-flex align-items-start mt-4 p-3 rounded bg-light">
        <i className="material-icons mr-2">info_outline</i>
        <span>
          A Central IA reúne mais de 10 inteligências artificiais de uso geral, indicadas para
          tarefas do cotidiano. Para demandas relacionadas às contratações públicas, recomendamos o
          uso das soluções especializadas do ConLicitação:{' '}
          <strong>Consultor Jurídico, Dr. Licita, Resumo do Edital e Pergunte ao Edital</strong>,
          desenvolvidas com curadoria de conteúdo, tratamento de dados e agentes de IA próprios.
        </span>
      </small>
    </Container>
  );
};

export default CentralIaChatArea;
